import { useCallback, useState } from "react";
import {
  type JobResponse,
  deleteProcessingResultArtifact,
  defaultApiClient,
} from "@taxassist/api-client";
import { deleteJobFile } from "../utils/fileUtils";
import {
  getCachedFilePreviewUrl,
  getCachedFileDownloadUrl,
} from "../utils/filePreviewUtils";

import type { FileItem, UploadedFileType } from "../components/jobs/fileList";

export interface FileActionsOptions {
  job: JobResponse | null;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
  onPreviewOpen?: (file: FileItem, files: FileItem[], index: number) => void;
  onFileUpdate?: () => void; // Called after file is updated (e.g., type change) to trigger data refresh
}

export interface FileActions {
  handleDownload: (file: FileItem) => Promise<void>;
  handlePreview: (file: FileItem, files: FileItem[], index: number) => void;
  handleDelete: (
    file: FileItem,
    fileType: "uploaded" | "processing",
  ) => Promise<void>;
  handleUpdateFileType: (
    file: FileItem,
    newType: UploadedFileType,
  ) => Promise<void>;
  handleUpdateBucket: (file: FileItem, newBucket: string) => Promise<void>;
  getFileUrl: (
    file: FileItem,
    fileType: "uploaded" | "processing",
  ) => Promise<string>;
}

/**
 * Custom hook to centralize file action logic for both uploaded files and processing results.
 * This eliminates duplication between UploadedFilesList and ProcessingArtifacts components.
 */
export function useFileActions(options: FileActionsOptions): FileActions {
  const { job, onSuccess, onError, onPreviewOpen, onFileUpdate } = options;
  const [urlCache] = useState(new Map<string, string>());

  /**
   * Generates or retrieves a cached presigned URL for file access.
   * Handles both uploaded files and processing results.
   */
  const getFileUrl = useCallback(
    async (
      file: FileItem,
      fileType: "uploaded" | "processing",
    ): Promise<string> => {
      // Check for required parameters
      if (!file.s3Key) {
        console.error("File missing s3Key:", file);
        throw new Error(`File ${file.fileName} is missing s3Key for preview`);
      }

      const cacheKey = `${fileType}-${file.s3Key}`;

      // Check cache first
      if (urlCache.has(cacheKey)) {
        return urlCache.get(cacheKey)!;
      }

      try {
        if (!job) {
          throw new Error("Job context is required for file operations");
        }

        // Generate presigned URL - convert FileItem to UploadedFile format
        const uploadedFile = {
          fileName: file.fileName,
          s3Key: file.s3Key,
          fileSize: file.fileSize,
          contentType: file.contentType,
          uploadedAt: file.uploadedAt,
        };

        const url = await getCachedFilePreviewUrl(uploadedFile);

        // Cache the URL
        urlCache.set(cacheKey, url);
        return url;
      } catch (error) {
        console.error("Failed to get file URL:", error);
        const errorMessage = `Failed to get URL for ${file.fileName}`;
        throw new Error(errorMessage);
      }
    },
    [job, urlCache],
  );

  /**
   * Handles file download by generating a presigned URL with proper download headers.
   * Uses dedicated download endpoints for better download experience.
   */
  const handleDownload = useCallback(
    async (file: FileItem) => {
      try {
        if (!job) {
          throw new Error("Job context is required for file downloads");
        }

        // Determine if this is a processing result based on metadata or S3 key pattern
        const isProcessingResult = file.metadata?.isProcessingResult;

        // Convert FileItem to UploadedFile format for the download utility
        const uploadedFile = {
          fileName: file.fileName,
          s3Key: file.s3Key,
          fileSize: file.fileSize,
          contentType: file.contentType,
          uploadedAt: file.uploadedAt,
        };

        // Get the download URL using the new download endpoints
        const downloadUrl = await getCachedFileDownloadUrl(
          uploadedFile,
          job.firmId,
          job.jobId,
          isProcessingResult,
        );

        // Direct navigation to the download URL - no need to fetch and create blob
        // since the backend provides proper Content-Disposition headers
        const anchor = document.createElement("a");
        anchor.style.display = "none";
        anchor.href = downloadUrl;
        anchor.download = file.fileName; // Fallback in case backend headers don't work

        // Append to body, click, and remove
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);

        onSuccess?.(`Downloading ${file.fileName}...`);
      } catch (error) {
        console.error("Failed to download file:", error);
        const errorMessage = `Failed to download ${file.fileName}`;
        onError?.(errorMessage);
        throw error; // Re-throw to allow component-level error handling
      }
    },
    [job, onSuccess, onError],
  );

  /**
   * Handles file preview by opening the file viewer dialog.
   * Delegates to the parent component's preview handler.
   */
  const handlePreview = useCallback(
    (file: FileItem, files: FileItem[], index: number) => {
      onPreviewOpen?.(file, files, index);
    },
    [onPreviewOpen],
  );

  /**
   * Handles file deletion with appropriate API call based on file type.
   * Includes proper error handling and user feedback.
   */
  const handleDelete = useCallback(
    async (file: FileItem, fileType: "uploaded" | "processing") => {
      if (!job) {
        onError?.("Job context is required for deletion");
        return;
      }

      try {
        if (fileType === "processing") {
          // Delete processing result artifact
          await deleteProcessingResultArtifact(job.jobId, file.id);
        } else {
          // Delete uploaded file
          await deleteJobFile(job.firmId, job.jobId, file.s3Key);
        }

        onSuccess?.(`Successfully deleted ${file.fileName}`);
        // Trigger file update callback to refresh the UI
        onFileUpdate?.();
      } catch (error) {
        console.error("Failed to delete file:", error);
        onError?.(`Failed to delete ${file.fileName}`);
        throw error; // Re-throw to allow component-level handling
      }
    },
    [job, onSuccess, onError, onFileUpdate],
  );

  /**
   * Handles updating the document type of an uploaded file.
   * Only available for uploaded files in ops context.
   */
  const handleUpdateFileType = useCallback(
    async (file: FileItem, newType: UploadedFileType) => {
      if (!job) {
        onError?.("Job context is required for updating file type");
        return;
      }

      try {
        await defaultApiClient({
          url: `/ops/jobs/${job.jobId}/uploaded-files`,
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          params: { s3Key: file.s3Key },
          data: { documentType: newType },
        });

        onSuccess?.(`Successfully updated file type for ${file.fileName}`);
        onFileUpdate?.(); // Trigger refresh to show updated data
      } catch (error) {
        console.error("Failed to update file type:", error);
        onError?.(`Failed to update file type for ${file.fileName}`);
        throw error; // Re-throw to allow component-level handling
      }
    },
    [job, onSuccess, onError, onFileUpdate],
  );

  /**
   * Handles updating the expected bucket of an uploaded file.
   * Only available for uploaded files in ops context.
   */
  const handleUpdateBucket = useCallback(
    async (file: FileItem, newBucket: string) => {
      if (!job) {
        onError?.("Job context is required for updating bucket");
        return;
      }

      try {
        await defaultApiClient({
          url: `/ops/jobs/${job.jobId}/uploaded-files`,
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          params: { s3Key: file.s3Key },
          data: { expectedBucket: newBucket },
        });

        onSuccess?.(`Successfully updated bucket for ${file.fileName}`);
        onFileUpdate?.(); // Trigger refresh to show updated data
      } catch (error) {
        console.error("Failed to update bucket:", error);
        onError?.(`Failed to update bucket for ${file.fileName}`);
        throw error; // Re-throw to allow component-level handling
      }
    },
    [job, onSuccess, onError, onFileUpdate],
  );

  return {
    handleDownload,
    handlePreview,
    handleDelete,
    handleUpdateFileType,
    handleUpdateBucket,
    getFileUrl,
  };
}
